import {
  ComponentDescriptor,
  PageLayout,
} from '../../../components/PageBuilder';
import {
  GetComposedForm,
  GetComposedForm_composedForm_questions as Question,
} from '../../../services/comparator-api/graphql/types';
import {
  FormField,
  ValidatorDescriptor,
  TransformerDescriptor,
} from '../../../state-machines/form-machine';
import { Step } from '../../../state-machines/wizard-machine';
import { Validators, Expressions, sanitizeName } from './expressions';
import { ValidatorFn } from '../../../config/validators';
import { validatorsMapping } from './mappings/validatorsMapping';
import { MouseEvent } from 'react';
import {
  transformerMapping,
  transformerMappingWithName,
} from './mappings/transformerMapping';
export interface GetStepsFromQuestions {
  (questions: GetComposedForm['composedForm']['questions']): Step[];
}

export type BaseValidator = {
  message: string;
  type: string;
};

export type LengthValidator = BaseValidator & {
  type: 'length';
  // the minimum length of the value (if null, no minimum length)
  min: null | number;
  // the maximum length of the value (if null, no maximum length)
  max: null | number;
};

export type RangeValidator = BaseValidator & {
  type: 'range';
  from: null | string;
  to: null | string;
};

export type RegexValidator = BaseValidator & {
  type: 'regex';
  pattern: string;
};

export type GenericValidator = BaseValidator & {
  type: 'generic';
  expression: Expressions.Descriptor;
};

export type ValidatorDescriptorOrFn =
  | ValidatorDescriptor
  | ReturnType<ValidatorFn>;

const parseValidator = (
  validator: BaseValidator
): ValidatorDescriptorOrFn[] | undefined => {
  switch (validator.type) {
    case 'generic':
      return [parseGenericValidator(validator as GenericValidator)];
    case 'regex':
      return parseRegexValidator(validator as RegexValidator);
    case 'range':
      return parseRangeValidator(validator as RangeValidator);
    case 'length':
      return parseLengthValidator(validator as LengthValidator);
    default:
      return undefined;
  }
};

export const parseGenericValidator = (
  validator: GenericValidator
): ReturnType<ValidatorFn> => {
  const booleanExpressionFn = Expressions.parse(validator.expression);

  return Validators.get(booleanExpressionFn, validator.message);
};

const parseRegexValidator = (
  validator: RegexValidator
): ValidatorDescriptor[] => {
  // TODO: check how modifiers should be handled
  return [{ fn: 'regex', args: [validator.message, validator.pattern] }];
};

const parseRangeValidator = (
  _validator: RangeValidator
): ValidatorDescriptor[] => {
  // TODO: figure out how we are supposed to differenciate decimal, dates (particular format?) etc...
  // I think we could cover most bases by running Number and Date.parse, the only one I don't think we could cover is the (times)??
  // I don't quite get what times is supposed to be, my first thought was 22:00:00 ?
  return [];
};

export const parseLengthValidator = (
  validator: LengthValidator
): ValidatorDescriptor[] => {
  const validators: ValidatorDescriptor[] = [];
  if (validator.min) {
    validators.push({
      fn: 'minLength',
      args: [validator.message, validator.min],
    });
  }
  if (validator.max) {
    validators.push({
      fn: 'maxLength',
      args: [validator.message, validator.min],
    });
  }
  return validators;
};

const parseValidators = (
  validators: BaseValidator[] | null
): ValidatorDescriptorOrFn[] => {
  if (!validators) return [];

  let parsedValidators: ValidatorDescriptorOrFn[] = [];

  for (const validator of validators) {
    if ((validator as any).name === 'validCbEmail') return [];
    const parsedValidator = parseValidator(validator) ?? [];

    if (Array.isArray(parsedValidator))
      parsedValidators = [...parsedValidators, ...parsedValidator];
    else parsedValidators.push(parsedValidator as ReturnType<ValidatorFn>);
  }

  return parsedValidators;
};

const getErrorMessageFromQueryType = (queryType: string): string => {
  // TODO: figure out if there are err msgs for queryTypes
  if (queryType === 'date') return '';
  return '';
};

const parseTypeValidator = (
  queryType: string
): ValidatorDescriptorOrFn | null => {
  const validatorDescriptor = validatorsMapping[queryType];
  if (validatorDescriptor) {
    if (typeof validatorDescriptor === 'function')
      return validatorDescriptor(getErrorMessageFromQueryType(queryType));
    return validatorDescriptor;
  }
  return null;
};

const parseTypeTransformers = (
  queryType: string,
  name: string
): TransformerDescriptor | null => {
  if (transformerMapping[queryType]) {
    return transformerMapping[queryType];
  }

  if (transformerMappingWithName[name]) {
    return transformerMappingWithName[name];
  }

  return null;
};

export const getDescriptorsFromLayout = () => undefined;

export const getValidateAs = (q: Question) => {
  if (q.valueType === 'date') return (v) => v?.split('-').reverse().join('-');
  return undefined;
};

const parseQuestion = (q: Question | undefined, f: FormField, product) => {
  if (!q) return undefined;

  const options = q.valueOptions
    ? q.valueOptions.map((opt) => ({
        label: opt.display,
        value: opt.value,
        extraLabel: opt.extraDisplayExpression
          ? Expressions.parse(opt.extraDisplayExpression)(
              `{ "data": ${JSON.stringify(product)} }`,
              []
            )
          : null,
        extraDisplay: opt.extraDisplay,
        description: opt.description,
        cost: opt.custom?.cost,
        discountDurationInMonths: opt.custom?.discountDurationInMonths,
        discountTariff: opt.custom?.discountTariff,
      }))
    : undefined;

  const validators = parseValidators(q.validators) ?? [];
  const transformers: TransformerDescriptor[] = [];
  const typeValidator = parseTypeValidator(q.valueType);
  const typeTransformer = parseTypeTransformers(q.valueType, q.name);

  if (typeValidator) validators.push(typeValidator);
  if (typeTransformer) transformers.push(typeTransformer);

  const field: FormField = {
    ...f,
    name: sanitizeName(q.name),
    label: q.label,
    subLabel: q?.subLabel,
    value: q.defaultValue ?? '',
    options,
    validator: validators as FormField['validator'],
    validateAs: getValidateAs(q),
    meta: {
      originalQuestion: q,
      originalDescriptor: f?.meta?.originalDescriptor,
    },
    transformers,
  };

  return field;
};

export type GroupLayoutElement = {
  type: 'group';
  elements: LayoutElement[];
};

export type QuestionLayoutElement = {
  type: 'question';
  name: string;
};

export type LabelLayoutElement = {
  type: 'label' | 'generic';
  text: string;
  textExpression?: Expressions.Descriptor;
};

export type GenericLayoutElement = {
  type: 'generic';
};

export type LayoutElement = {
  component: string;
  custom: object | null;
  visibleExpression: Question['visibleExpression'];
} & (
  | GroupLayoutElement
  | QuestionLayoutElement
  | LabelLayoutElement
  | GenericLayoutElement
);

export const crawlLayoutElement = (
  elmt: LayoutElement,
  predicate: (elmt: LayoutElement) => boolean,
  results: LayoutElement[] = []
) => {
  if (Object.prototype.hasOwnProperty.call(elmt, 'elements')) {
    const elements = (elmt as GroupLayoutElement).elements;

    for (const subElmt of elements) {
      if (predicate(subElmt)) results.push(subElmt);

      results = results.concat(crawlLayoutElement(subElmt, predicate, []));
    }
  }

  // If notice any bug change uncomment these. (Removes duplicate fields)
  //   results = results.concat(crawlLayoutElement(subElmt, predicate, results));
  // }

  // return results.filter(
  //   (v, i, a) => a.findIndex((v2) => v2.name === v.name) === i
  // );

  return results;
};

const layoutElementsMapping = {
  list: {
    type: 'Div',
    props: {
      display: 'flex',
      css: {
        columnGap: 3,
        '@media (max-width: 767px)': {
          flexDirection: 'column',
        },
      },
    },
  },
  grid: {
    type: 'Row',
    props: {
      css: {
        columnGap: 6,
      },
    },
  },
  'horizontal-rule': {
    type: 'Separator',
    props: {
      px: 0,
      py: 6,
      lineProps: { width: '100%', color: 'supplementary.6' },
    },
  },
  heading: {
    type: 'Heading',
    props: {
      variant: 'H4',
    },
  },
  warning: (el) => ({
    type: 'Banner',
    props: {
      className: 'alertBanner',
      iconName: 'alert',
      variant: 'bannerWarning',
      body: el.text,
      my: 3,
    },
    meta: { propsMap: { text: 'body' } },
  }),
  'info-banner': (el) => ({
    type: 'Banner',
    props: {
      iconName: 'idea',
      variant: 'bannerSuggestion',
      body: el.text,
      my: 3,
    },
    meta: { propsMap: { text: 'body' } },
  }),
  usp: {
    type: 'Usp',
    props: {
      items: [],
      my: 3,
    },
  },
  formDataViewer: (el) => ({
    type: 'DynamicFormDataViewer',
    props: {
      paths: el.custom.paths.map((e) => e.replaceAll('.', '-')),
    },
  }),
};

export const getInputsMapping = (question?: Question) => {
  const _containerProps = { py: 2 };
  return {
    dropdown: {
      type: 'Inputs.Select',
      props: {
        _tooltip: question?.description,
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
      },
    },
    checkbox: {
      type: 'CustomCheckboxWrapper',
      props: {
        _tooltip: question?.description,
        uncheckedValue: 'false',
        checkedValue: 'true',
        variant: 'main',
        _successProps: 'main',
        labelPosition: 'right',
        _containerProps: {
          ..._containerProps,
          _labelProps: {
            fontWeight: 600,
          },
          flexDirection: 'row-reverse',
          justifyContent: 'flex-end',
          flexWrap: 'wrap',
          css: {
            '> p': {
              order: 3,
            },
            '> span': {
              order: 2,
            },
            '> div': {
              width: 'calc(100% - 32px)',
              order: 1,
              ml: 2,
            },
          },
        },
        _labelProps: {
          variant: 'sideLabel',
        },
      },
    },
    datepicker: {
      type: 'Inputs.DateInput',
      props: {
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
        _tooltip: question?.description,
      },
    },
    integer: {
      type: 'BasicInputWrapper',
      props: {
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
        autoComplete: 'new-password',
        _tooltip: question?.description,
        placeholder: question?.placeholder,
      },
    },
    text: {
      type: 'Inputs.BasicInput',
      props: {
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
        autoComplete: 'new-password',
        _tooltip: question?.description,
        placeholder: question?.placeholder,
      },
    },
    radiobuttonlist: {
      type: 'CustomRadioGroupWrapper',
      props: {
        variant: 'main',
        containerProps: {
          variant: 'radio',
          flexDirection: 'row-reverse',
          justifyContent: 'flex-end',
        },
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
        _tooltip: question?.description,
        // TODO: find a solution without using a timeout
        onClick: (e: MouseEvent) =>
          setTimeout(() => (e.target as HTMLElement).blur(), 100),
      },
    },
    ExtraOptionsRadio: {
      type: 'ExtraOptionsRadio',
      props: {
        _label: question?.label,
        name: question?.name,
        variant: 'main',
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
      },
    },
    ExtraOptionsCheckbox: {
      type: 'ExtraOptionsCheckbox',
      props: {
        label: question?.label,
        name: question?.name,
        question: question,
        variant: 'main',
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
      },
    },
    ExtraOptionsSelect: {
      type: 'ExtraOptionsSelect',
      props: {
        label: question?.label,
        name: question?.name,
        question: question,
        variant: 'main',
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
      },
    },
    textarea: {
      type: 'Inputs.Textarea',
      props: {
        variant: 'main',
        _containerProps,
        _tooltip: question?.description,
        _labelProps: {
          fontWeight: 600,
        },
      },
    },
    tileradiogroup: {
      type: 'Inputs.TileRadioGroup',
      props: {
        _containerProps,
        _labelProps: {
          fontWeight: 600,
        },
        _tooltip: question?.description,
        radios:
          question?.valueOptions?.map((option) => ({
            text: option.display,
            value: option.value,
            children: [],
          })) || [],
      },
    },
  };
};

export const getCustomMapping = (
  _el: LayoutElement,
  q: Question | undefined,
  product?
) => ({
  columns: 'cols',
  direction: (value: string) => {
    switch (value) {
      case 'horizontal':
        return { direction: 'horizontal' };
      case 'vertical':
        return { direction: 'vertical' };
      case 'left':
        return { flexDirection: 'row-reverse' };
      case 'up':
        return { flexDirection: 'column-reverse' };
      case 'down':
        return { flexDirection: 'column' };
      default:
        return {};
    }
  },
  grow: (value: string) => (value ? { flexGrow: Number(value) } : {}),
  bold: (value) => (value ? { fontWeight: 'bold' } : {}),
  items: 'items',
  bottomText: 'bottomText',
  title: 'title',
  variant: 'variant',
  labelContentType: (value) => {
    let props = {};
    let modifiedLabel = null;
    if (q && q['labelExpression']) {
      modifiedLabel = Expressions.parse(q['labelExpression'])(
        `{ "data": ${JSON.stringify(product)} }`,
        []
      );
    }

    if (value === 'html') {
      props = {
        _label: undefined,
        _labelProps: {
          variant: 'lists.applyPageTermsAndPoliciesList',
          dangerouslySetInnerHTML: {
            __html: modifiedLabel ? modifiedLabel : q?.label,
          },
          children: undefined,
        },
      };
    }
    return props;
  },
  styling: (value: string) => {
    if (value === 'info') {
      return {
        css: {
          background: '#F5F5F5',
          borderRadius: '16px',
          padding: '16px 24px',
          h1: {
            fontSize: '11px',
            fontWeight: 'bold',
            color: '#535862',
          },
        },
      };
    }
    if (value === 'card') {
      return {
        css: {
          background: '#FFFFFF',
          borderRadius: '16px',
          padding: '16px 24px',
          marginTop: '20px',
          marginBottom: '10px',
          boxShadow: '0px 0px 20px rgba(0, 0, 0, 0.15)',
        },
      };
    }
    return {};
  },
});

const parseCustomProps = (el: LayoutElement, q: Question, p?) => {
  const { custom } = el;
  const customMapping = getCustomMapping(el, q, p);
  let props = {};
  if (!custom) return props;
  Object.keys(custom).forEach((k) => {
    if (typeof customMapping[k] === 'function')
      props = { ...props, ...customMapping[k](custom[k]) };
    if (typeof customMapping[k] === 'string')
      props[customMapping[k]] = custom[k];
  });
  return props;
};

const getQuestionFromElement = (
  questions: Question[],
  el: LayoutElement
): Question | undefined => {
  return questions.find((q) => {
    return (
      (el as QuestionLayoutElement).name &&
      q.name === (el as QuestionLayoutElement).name
    );
  });
};

const parseLayoutElement = (
  el: LayoutElement,
  defaultComponentDescriptor: ComponentDescriptor,
  questions: Question[]
): ComponentDescriptor => {
  const { component } = el;
  let baseComponent =
    layoutElementsMapping[component] ?? defaultComponentDescriptor;
  baseComponent =
    typeof baseComponent === 'function' ? baseComponent(el) : baseComponent;
  const question = getQuestionFromElement(questions, el);

  return {
    type: 'DynamicLayoutElement',
    props: {
      originalDescriptor: el,
      originalQuestion: question,
      componentDescriptor: {
        ...baseComponent,
        props: {
          ...baseComponent.props,
          ...parseCustomProps(el, question as Question),
        },
      },
    },
  };
};

export const parseElement = (
  el: LayoutElement,
  formName: string,
  questions: Question[],
  stepsCallback: (
    formName: string,
    fieldName: string,
    originalDescriptor: LayoutElement
  ) => void,
  customBlur?: (e) => void,
  selectCustomOnChange?: (e) => void,
  customCheckboxOnChange?: (e, q) => void,
  selectedProduct?
): ComponentDescriptor | undefined => {
  const question = getQuestionFromElement(questions, el);
  const shouldTriggerAPICall = question?.custom?.postback;

  const inputTypeMapping = {
    number: 'number',
    phoneNumber: 'tel',
    email: 'email',
    string: 'string',
    boolean: 'boolean',
    integer: 'tel',
  };

  if (el.type === 'group') {
    const parsedGroupComponent = parseLayoutElement(
      el,
      {
        type: 'Div',
        props: {},
      },
      questions
    );

    return {
      ...parsedGroupComponent,
      props: {
        ...parsedGroupComponent.props,
        componentDescriptor: {
          ...parsedGroupComponent.props.componentDescriptor,
          props: {
            ...parsedGroupComponent.props.componentDescriptor.props,
          },
          _children: parseLayoutElements(
            el.elements,
            formName,
            questions,
            stepsCallback,
            customBlur,
            selectCustomOnChange,
            customCheckboxOnChange
          ),
        },
      },
    };
  }
  if (el.type === 'label' || el.type === 'generic') {
    const parsedLabelComponent = parseLayoutElement(
      el,
      {
        type: 'P',
        props: {},
      },
      questions
    );
    return {
      ...parsedLabelComponent,
      props: {
        ...parsedLabelComponent.props,
        componentDescriptor: {
          ...parsedLabelComponent.props.componentDescriptor,
          props: {
            ...parsedLabelComponent.props.componentDescriptor.props,
            children: el.type === 'label' ? el.text : undefined,
          },
        },
      },
    };
  }
  if (el.type === 'question') {
    if (!question) return;
    stepsCallback(formName, el.name, el);

    let component = {
      ...getInputsMapping()[el.component],
      props: {
        type: inputTypeMapping?.[question.valueType],
        ...getInputsMapping(question)[el.component].props,
        ...parseCustomProps(el, question, selectedProduct),
        customBlur: (e) => {
          shouldTriggerAPICall &&
            typeof customBlur === 'function' &&
            customBlur(e);
        },
        selectCustomOnChange: (e) => {
          shouldTriggerAPICall &&
            typeof selectCustomOnChange === 'function' &&
            selectCustomOnChange(e);
        },
        // Passing customBlur to be called on radio button change since they have the same functionality
        customRadioChange: (e) => {
          shouldTriggerAPICall &&
            typeof customBlur === 'function' &&
            customBlur(e);
        },
        customCheckboxChange: (e) => {
          shouldTriggerAPICall &&
            typeof customCheckboxOnChange === 'function' &&
            customCheckboxOnChange(e, question);
        },
        shouldTriggerAPICall,
      },
    };
    if (!component) {
      throw new Error(
        el.component + ' not found in ' + Object.keys(getInputsMapping())
      );
    }
    const propMap: object = { '.': 'meta.props' };
    return {
      type: 'DynamicLayoutElement',
      props: {
        originalQuestion: getQuestionFromElement(questions, el),
        originalDescriptor: el,
        componentDescriptor: {
          type: 'FormInput',
          props: {
            name: sanitizeName(el.name),
            component,
            propMap,
          },
        },
      },
    };
  }
  return undefined;
};

const parseLayoutElements = (
  elements: LayoutElement[],
  formName: string,
  questions: Question[],
  stepsCallback: (
    formName: string,
    fieldName: string,
    originalDescriptor: LayoutElement
  ) => void,
  customBlur?: (e) => void,
  selectCustomOnChange?: (e) => void,
  customCheckboxOnChange?: (e, q) => void,
  selectedProduct?
): ComponentDescriptor[] => {
  const descriptors: ComponentDescriptor[] = [];
  for (const el of elements) {
    const parsedElement = parseElement(
      el,
      formName,
      questions,
      stepsCallback,
      customBlur,
      selectCustomOnChange,
      customCheckboxOnChange,
      selectedProduct
    );
    if (parsedElement) descriptors.push(parsedElement);
  }
  return descriptors;
};

export const parseLayout = (
  layout: GetComposedForm['composedForm']['layout'],
  questions: Question[],
  stepsCallback: (
    formName: string,
    fieldName: string,
    originalDescriptor: LayoutElement
  ) => void,
  customBlur?: (e) => void,
  selectCustomOnChange?: (e) => void,
  customCheckboxOnChange?: (e, q) => void,
  selectedProduct?
) => {
  const groupedDescriptors: PageLayout[] = [];
  for (const step of layout.steps ?? []) {
    const name = step.name;
    const group: PageLayout = {
      name,
      components:
        [
          {
            type: 'Div',
            _children: parseLayoutElements(
              step.elements,
              name,
              questions,
              stepsCallback,
              customBlur,
              selectCustomOnChange,
              customCheckboxOnChange,
              selectedProduct
            ),
          },
        ] ?? [],
    };
    groupedDescriptors.push(group);
  }
  return groupedDescriptors;
};

export function parseBool(v) {
  if (Expressions.isTrue(v)) return true;
  if (Expressions.isFalse(v)) return false;
  return !!v;
}

export const parseComposedForm = (
  { questions, layout },
  customBlur,
  selectCustomOnChange,
  customCheckboxOnChange,
  selectedProduct?
) => {
  const steps = {};
  const stepsCallback = (
    formName: string,
    fieldName: string,
    originalDescriptor: LayoutElement
  ) => {
    const newField = { name: fieldName, meta: { originalDescriptor } };
    if (steps[formName]) {
      steps[formName].fields.push(newField);
    } else {
      steps[formName] = { fields: [newField] };
    }
  };

  const componentDescriptors: PageLayout[] = parseLayout(
    layout,
    questions,
    stepsCallback,
    customBlur,
    selectCustomOnChange,
    customCheckboxOnChange,
    selectedProduct
  );

  return {
    steps: Object.keys(steps).map((name) => ({
      name,
      fields: steps[name].fields.map((f) => {
        const relatedQuestion = questions.find((q) => q.name === f.name);

        return parseQuestion(relatedQuestion, f, selectedProduct);
      }),
    })),
    layout: componentDescriptors,
  };
};

export const jsonObjectBuilder = (steps) => {
  let jsonObject = {};
  const fields = steps.flatMap((s) => {
    return s.fields;
  });

  const enabledQuestions = fields.filter((f: FormField) => {
    const question = f.meta?.originalQuestion ?? null;

    if (!question) return false;

    return Expressions.isTrue(
      Expressions.parse(question.enabledExpression)(
        fields.find((f) => f.name === sanitizeName(question.name))?.value,
        fields
      )
    );
  });

  let questionsAnswers = enabledQuestions.map((f) => {
    const question = f.meta?.originalQuestion;
    const stateValue = fields.find(
      (field) => field.name === sanitizeName(question.name)
    )?.value;

    let value =
      stateValue ||
      question.defaultValue ||
      (question.defaultValueExpression
        ? Expressions.parse(question.defaultValueExpression)('')
        : null);

    if (question?.valueMapping) {
      value = Expressions.parse(f.valueMapping)(value);
    }

    switch (question.queryType) {
      case 'boolean':
        value = parseBool(value);
        break;
      case 'integer':
        value = parseInt(value);
        break;
      case 'decimal':
        value = parseFloat(value);
        break;
    }
    return {
      question: question,
      answer: value,
    };
  });

  // Create the final JSON object based on the given questions and answers
  jsonObject = simplifyObject(buildJsonObject(questionsAnswers));

  // Transform effectiveDateNotToday to YYYY-MM-DD format for BE while keeping UI in DD-MM-YYYY (If user choose future-date)
  const effectiveDate = fields.find(
    (field) => field.name === 'effectiveDate'
  )?.value;

  const effectiveDateNotToday = fields.find(
    (field) => field.name === 'effectiveDateNotToday'
  )?.value;

  const hasEffectiveDate = effectiveDate || effectiveDateNotToday;
  const isCBAPP = window.navigator.userAgent.includes('CBAPP');

  return {
    ...jsonObject,
    ...(hasEffectiveDate && {
      effectiveDate: effectiveDate
        ? effectiveDate.split('-').reverse().join('-')
        : effectiveDateNotToday.split('-').reverse().join('-'),
    }),
    process: {
      ...(isCBAPP && {
        reference: 'CBAPP',
      }),
    },
  };
};

// Build a JSON object based on the questions and answers by mapping answers to the specified JSON paths
const buildJsonObject = (questionsAnswers) => {
  let document = {};

  // Iterate through each question-answer pair
  for (const qa of questionsAnswers) {
    if (qa.answer === null) {
      continue;
    }

    const answer = qa.answer;
    const mappings = qa.question.mapping;

    // Update the document for each mapping of the current question
    for (const mapping of mappings) {
      document = putValue(document, mapping, answer);
    }
  }

  return document;
};

const simplifyObject = (obj) => {
  const simplified = {};

  for (const key in obj) {
    const match = key.match(/^(.+)\[(\d+)\]$/);

    if (match) {
      const [, newKey, index] = match;
      if (!simplified[newKey]) {
        simplified[newKey] = {};
      }
      if (Number(index) > 0 || Array.isArray(simplified[newKey])) {
        if (!Array.isArray(simplified[newKey])) {
          const firstElement = simplified[newKey];
          simplified[newKey] = [firstElement];
        }
        simplified[newKey][index] = simplifyObject(obj[key]);
      } else {
        simplified[newKey] = simplifyObject(obj[key]);
      }
    } else if (typeof obj[key] === 'object' && obj[key] !== null) {
      simplified[key] = simplifyObject(obj[key]);
    } else {
      simplified[key] = obj[key];
    }
  }

  return simplified;
};

// Update the document by putting the given value at the specified JSON path
const putValue = (document, jsonPath, value) => {
  if (!document) return null;
  const path = parsePath(jsonPath);

  let currentObject = document;
  for (let i = 0; i < path.length; i++) {
    const node = path[i];
    const isLast = path.length - 1 === i;
    const isMandatory = !node.isOptional;

    if (node.name) {
      const { name } = node;

      if (isLast) {
        putLastValue(currentObject, name, value);
      } else if (isMandatory && currentObject[name] == null) {
        currentObject[name] = getDefaultValue(path[i + 1]);
      }
      currentObject = currentObject[name];
    } else if (node.index) {
      const index = parseInt(node.index);

      if (isLast) {
        putLastValue(currentObject, index, value);
      } else if (isMandatory && currentObject.length <= index) {
        currentObject.push(getDefaultValue(path[i + 1]));
      }

      currentObject = currentObject[index];
    }
  }

  return document;
};

const putLastValue = (currentObject, nameOrIndex, value) => {
  if (!Object.prototype.hasOwnProperty.call(currentObject, nameOrIndex)) {
    currentObject[nameOrIndex] = value;
  } else {
    if (!Array.isArray(currentObject[nameOrIndex])) {
      currentObject[nameOrIndex] = [currentObject[nameOrIndex]];
    }

    if (!currentObject[nameOrIndex].includes(value)) {
      currentObject[nameOrIndex].push(value);
    }
  }
};

const parsePath = (path: string) => {
  // Regular expression to match named groups, indexed groups, and optional parts
  const regex =
    /(?:(?<named>[a-zA-Z0-9]+)|\[(?<indexed>\d+)\])(?<optional>\??)/;

  const splitedPath = path.split('.');

  // Extract matched groups and their properties
  return splitedPath
    .flatMap((item) => [item.match(regex)])
    .map((item, index) => {
      const matchedIndex = item?.groups?.indexed;
      const isOptional = item?.groups?.optional;

      return {
        name: splitedPath[index],
        index: matchedIndex,
        isOptional: isOptional,
      };
    });
};

// Return the default value based on the presence of an index
const getDefaultValue = (node) => (node.index ? [] : {});
